/**
 * Feedback & Fixes — every "Submit a bug" / "Suggest a feature" from every
 * client, one queue, with the AI fix pipeline's state visible and controllable.
 *
 * The pipeline (bugs): report arrives → AI triage grades it and, if it looks
 * agent-fixable, queues an automatic fix attempt → the fix agent works in an
 * isolated staging copy, runs the full test suite, pushes a branch and leaves a
 * plain-language note → the operator reads the note and clicks Approve (ships,
 * deploys) or Decline (branch abandoned). Approve is the ONLY door to
 * production.
 *
 * Features skip the pipeline: triage groups duplicates and the operator marks
 * them planned/declined.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bug, Lightbulb } from "lucide-react";
import { useApiClient } from "../lib/api-context.js";
import { useToast } from "../lib/toast.js";
import { Card } from "../components/cards.js";
import { Segmented } from "../components/Segmented.js";

interface FixRun {
  id: string;
  status: string;
  branch: string | null;
  noteMd: string | null;
  testsPassed: boolean | null;
  diffStat: string | null;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
}

interface Report {
  id: string;
  accountId: string;
  accountName: string;
  type: "bug" | "feature";
  /** 'user' (typed), 'ui_crash' (auto crash report), 'system' (watchdog). */
  source: string;
  page: string | null;
  name: string | null;
  email: string | null;
  description: string;
  link: string | null;
  hasAttachment: boolean;
  status: string;
  severity: string | null;
  area: string | null;
  aiSummary: string | null;
  duplicateOf: string | null;
  createdAt: string;
  fixRun: FixRun | null;
}

type TypeTab = "all" | "bug" | "feature";
type StageTab = "open" | "ready" | "shipped" | "closed";

const OPEN_STATUSES = new Set(["new", "triaged", "fix_queued", "fixing"]);
const CLOSED_STATUSES = new Set(["declined", "closed", "duplicate"]);

const STATUS_LABEL: Record<string, string> = {
  new: "new — awaiting triage",
  triaged: "triaged",
  fix_queued: "queued for AI fix",
  fixing: "AI is working on it",
  fix_ready: "fix ready — needs your approval",
  approved: "approved — shipping",
  shipped: "shipped",
  planned: "planned",
  declined: "declined",
  closed: "closed",
  duplicate: "duplicate",
};

export default function AgencyFeedbackPage(): React.ReactElement {
  const api = useApiClient();
  const qc = useQueryClient();
  const toast = useToast();
  const [typeTab, setTypeTab] = useState<TypeTab>("all");
  const [stageTab, setStageTab] = useState<StageTab>("open");
  const [expanded, setExpanded] = useState<string | null>(null);

  const status = useQuery({
    queryKey: ["account", "agency-status"],
    queryFn: () => api.get<{ isOperator: boolean }>("/account/agency-status"),
  });
  const isOperator = status.data?.isOperator ?? false;

  const q = useQuery({
    queryKey: ["admin", "feedback"],
    enabled: isOperator,
    queryFn: () => api.get<{ reports: Report[] }>("/admin/feedback"),
    refetchInterval: 60_000, // the queue moves on its own (triage, fix agent) — keep it live
  });
  const reports = useMemo(() => q.data?.reports ?? [], [q.data]);

  const act = useMutation({
    mutationFn: (input: { id: string; action: "approve" | "decline" | "status"; status?: string }) =>
      input.action === "status"
        ? api.post(`/admin/feedback/${input.id}/status`, { status: input.status })
        : api.post(`/admin/feedback/${input.id}/${input.action}`, {}),
    onSuccess: (_d, input) => {
      void qc.invalidateQueries({ queryKey: ["admin", "feedback"] });
      if (input.action === "approve") toast.success("Approved — the agent will merge and deploy it on its next pass.");
      else if (input.action === "decline") toast.success("Declined — the branch is abandoned, report back in the queue.");
      else if (input.status === "closed") toast.success("Marked fixed — cleared from the queue.");
      else toast.success("Updated.");
    },
    onError: () => toast.error("That didn't stick — try again."),
  });

  const counts = useMemo(() => {
    const real = reports.filter((r) => !CLOSED_STATUSES.has(r.status));
    return {
      awaitingApproval: reports.filter((r) => r.status === "fix_ready").length,
      inFlight: reports.filter((r) => r.status === "fixing" || r.status === "fix_queued").length,
      open: real.length,
      shipped: reports.filter((r) => r.status === "shipped").length,
    };
  }, [reports]);

  const visible = reports.filter((r) => {
    if (typeTab !== "all" && r.type !== typeTab) return false;
    if (stageTab === "open") return OPEN_STATUSES.has(r.status);
    if (stageTab === "ready") return r.status === "fix_ready" || r.status === "approved";
    if (stageTab === "shipped") return r.status === "shipped" || r.status === "planned";
    return CLOSED_STATUSES.has(r.status);
  });

  if (status.isLoading) return <p style={{ color: "var(--body)" }}>Loading…</p>;
  if (!isOperator) {
    return (
      <div>
        <h1 style={{ margin: 0, fontSize: 24 }}>Feedback & fixes</h1>
        <p style={{ color: "var(--body)", marginTop: 10 }}>This area is only available to agency operators.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={kicker}>Agency</div>
      <h1 style={{ margin: "7px 0 8px", fontSize: 27 }}>What are users telling us — and what's already being fixed?</h1>
      <p style={{ color: "var(--body)", fontSize: 14.5, maxWidth: 820, lineHeight: 1.5, marginTop: 0 }}>
        Every bug report and feature idea from every client lands here. Bugs that look fixable get an automatic fix attempt in a
        staging copy — tested, summarized, and held for your <b>Approve</b> before anything ships.
      </p>

      {/* Pipeline pulse */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
        {[
          { label: "awaiting your approval", n: counts.awaitingApproval, hot: counts.awaitingApproval > 0 },
          { label: "AI working / queued", n: counts.inFlight, hot: false },
          { label: "open reports", n: counts.open, hot: false },
          { label: "shipped", n: counts.shipped, hot: false },
        ].map((c) => (
          <div key={c.label} style={{ ...pulseBox, ...(c.hot ? pulseHot : {}) }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: c.hot ? "var(--accent)" : "var(--num)" }} className="dd-num">{c.n}</span>
            <span style={{ fontSize: 12, color: "var(--body)" }}>{c.label}</span>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 16, alignItems: "center" }}>
        <Segmented<StageTab>
          value={stageTab}
          onChange={setStageTab}
          options={[
            { value: "open", label: "Open" },
            { value: "ready", label: `For approval${counts.awaitingApproval ? ` (${counts.awaitingApproval})` : ""}` },
            { value: "shipped", label: "Shipped / planned" },
            { value: "closed", label: "Closed" },
          ]}
        />
        <Segmented<TypeTab>
          value={typeTab}
          onChange={setTypeTab}
          options={[
            { value: "all", label: "All" },
            { value: "bug", label: "Bugs" },
            { value: "feature", label: "Features" },
          ]}
        />
      </div>

      {q.isLoading && <p style={{ marginTop: 18, color: "var(--body)" }}>Loading the queue…</p>}
      {q.isError && <p style={{ marginTop: 18, color: "var(--bad)" }}>Couldn't load the queue. Refresh to retry.</p>}

      {q.data && visible.length === 0 && (
        <Card style={{ marginTop: 16 }}>
          <p style={{ color: "var(--body)", fontSize: 13.5, margin: 0 }}>
            {stageTab === "open"
              ? "Nothing open. When a client submits a bug or idea (sidebar → Submit a bug / Suggest a feature), it appears here within seconds."
              : "Nothing here yet."}
          </p>
        </Card>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 16 }}>
        {visible.map((r) => {
          const isOpen = expanded === r.id;
          const run = r.fixRun;
          return (
            <Card key={r.id} style={{ padding: 0 }}>
              <button type="button" onClick={() => setExpanded(isOpen ? null : r.id)} style={rowBtn} aria-expanded={isOpen}>
                {r.type === "bug" ? (
                  <Bug size={16} strokeWidth={1.8} style={{ color: "var(--bad)", flexShrink: 0 }} />
                ) : (
                  <Lightbulb size={16} strokeWidth={1.8} style={{ color: "#E5A83B", flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <div style={{ fontSize: 13.5, color: "var(--text)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {r.aiSummary ?? r.description.split("\n")[0]}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--body)", marginTop: 2 }}>
                    {r.accountName}
                    {r.page ? ` · ${r.page}` : ""} · {new Date(r.createdAt).toLocaleDateString()}
                    {r.name ? ` · from ${r.name}` : ""}
                  </div>
                </div>
                {r.source === "ui_crash" && <span style={sourceChip}>auto · crash report</span>}
                {r.source === "system" && <span style={sourceChip}>auto · system watchdog</span>}
                {r.source === "card_review" && <span style={cardReviewChip}>card review</span>}
                {r.severity && <span style={sevChip(r.severity)}>{r.severity}</span>}
                <span style={statusChip(r.status)}>{STATUS_LABEL[r.status] ?? r.status}</span>
                <span style={{ color: "var(--body-faint)", fontSize: 12 }}>{isOpen ? "▲" : "▼"}</span>
              </button>

              {isOpen && (
                <div style={{ padding: "0 16px 14px" }}>
                  <div style={detailBlock}>
                    <div style={detailLabel}>The report, in their words</div>
                    <div style={{ fontSize: 13, color: "var(--text)", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{r.description}</div>
                    {r.link && (
                      <div style={{ fontSize: 12.5, marginTop: 6 }}>
                        <a href={r.link} target="_blank" rel="noreferrer" style={{ color: "var(--accent)" }}>Attached link ↗</a>
                      </div>
                    )}
                    {r.hasAttachment && <div style={{ fontSize: 12, color: "var(--body-faint)", marginTop: 4 }}>Screenshot/clip was sent to the support inbox by email.</div>}
                    {r.area && <div style={{ fontSize: 12, color: "var(--body)", marginTop: 6 }}>AI triage: <b>{r.area}</b>{r.severity ? ` · ${r.severity}` : ""}</div>}
                    {r.duplicateOf && <div style={{ fontSize: 12, color: "var(--body)", marginTop: 4 }}>Marked as a duplicate of an earlier open report.</div>}
                  </div>

                  {run && (
                    <div style={{ ...detailBlock, marginTop: 10, borderColor: run.status === "fix_ready" ? "color-mix(in srgb, var(--accent) 40%, var(--border))" : "var(--border)" }}>
                      <div style={detailLabel}>
                        Automated fix attempt
                        {run.testsPassed === true && <span style={{ ...passBadge, marginLeft: 8 }}>all tests passed ✓</span>}
                        {run.testsPassed === false && <span style={{ ...failBadge, marginLeft: 8 }}>tests failed</span>}
                      </div>
                      {run.noteMd ? (
                        <div style={{ fontSize: 13, color: "var(--text)", whiteSpace: "pre-wrap", lineHeight: 1.55 }}>{run.noteMd}</div>
                      ) : run.status === "queued" || run.status === "running" ? (
                        <div style={{ fontSize: 13, color: "var(--body)" }}>
                          {run.status === "queued" ? "Waiting for the fix agent's next pass." : "The agent is working on it right now."}
                        </div>
                      ) : null}
                      {run.error && <div style={{ fontSize: 12.5, color: "var(--bad)", whiteSpace: "pre-wrap", marginTop: 6 }}>{run.error}</div>}
                      {(run.diffStat ?? run.branch) && (
                        <div style={{ fontSize: 12, color: "var(--body-faint)", marginTop: 8 }}>
                          {run.branch ? `branch ${run.branch}` : ""}
                          {run.diffStat ? ` · ${run.diffStat}` : ""}
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                    {r.status === "fix_ready" && (
                      <>
                        <button type="button" style={primaryBtn} disabled={act.isPending} onClick={() => act.mutate({ id: r.id, action: "approve" })}>
                          Approve & ship
                        </button>
                        <button type="button" style={ghostBtn} disabled={act.isPending} onClick={() => act.mutate({ id: r.id, action: "decline" })}>
                          Decline the fix
                        </button>
                      </>
                    )}
                    {r.type === "bug" && ["new", "triaged"].includes(r.status) && (
                      <button type="button" style={ghostBtn} disabled={act.isPending} onClick={() => act.mutate({ id: r.id, action: "status", status: "fix_queued" })}>
                        Queue an AI fix attempt
                      </button>
                    )}
                    {r.type === "feature" && ["new", "triaged"].includes(r.status) && (
                      <button type="button" style={ghostBtn} disabled={act.isPending} onClick={() => act.mutate({ id: r.id, action: "status", status: "planned" })}>
                        Mark planned
                      </button>
                    )}
                    {!CLOSED_STATUSES.has(r.status) && r.status !== "fix_ready" && r.status !== "approved" && (
                      <>
                        {/* One-click "done" once a bug's been fixed (e.g. fixed
                            by hand) — moves it out of the open queue. */}
                        <button type="button" style={{ ...primaryBtn, background: "var(--good)" }} disabled={act.isPending} onClick={() => act.mutate({ id: r.id, action: "status", status: "closed" })}>
                          ✓ {r.type === "bug" ? "Mark fixed" : "Mark done"}
                        </button>
                        <button type="button" style={ghostBtn} disabled={act.isPending} onClick={() => act.mutate({ id: r.id, action: "status", status: "declined" })}>
                          {r.type === "bug" ? "Not a real bug" : "Decline"}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

const kicker: React.CSSProperties = { color: "var(--accent)", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" };
const pulseBox: React.CSSProperties = {
  display: "flex", alignItems: "baseline", gap: 8, padding: "9px 14px", background: "var(--surface)",
  border: "1px solid var(--border)", borderRadius: "var(--r-sm)", boxShadow: "var(--shadow-card)",
};
const pulseHot: React.CSSProperties = { borderColor: "color-mix(in srgb, var(--accent) 45%, var(--border))", background: "color-mix(in srgb, var(--accent) 6%, var(--surface))" };
const rowBtn: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "13px 16px", background: "transparent",
  border: "none", cursor: "pointer", fontFamily: "inherit",
};
const sevChip = (sev: string): React.CSSProperties => ({
  fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", padding: "2px 8px", borderRadius: 9, flexShrink: 0,
  background: sev === "critical" ? "color-mix(in srgb, var(--bad) 18%, transparent)" : sev === "high" ? "color-mix(in srgb, var(--bad) 10%, transparent)" : "var(--surface-2)",
  color: sev === "critical" || sev === "high" ? "var(--bad)" : "var(--body)",
});
const statusChip = (status: string): React.CSSProperties => ({
  fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 9, whiteSpace: "nowrap", flexShrink: 0,
  background:
    status === "fix_ready" ? "color-mix(in srgb, var(--accent) 15%, transparent)"
    : status === "shipped" ? "color-mix(in srgb, var(--good) 15%, transparent)"
    : status === "fixing" || status === "fix_queued" ? "color-mix(in srgb, var(--accent) 8%, transparent)"
    : "var(--surface-2)",
  color: status === "fix_ready" ? "var(--accent)" : status === "shipped" ? "var(--good)" : "var(--body)",
});
const sourceChip: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 9, whiteSpace: "nowrap", flexShrink: 0,
  background: "var(--surface-2)", color: "var(--body)", border: "1px solid var(--border)",
};
// Card-review reports are operator-authored (Agency › Cards), not machine-filed —
// give them their own accent chip so they read distinctly from the "auto" sources.
const cardReviewChip: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 9, whiteSpace: "nowrap", flexShrink: 0,
  background: "color-mix(in srgb, var(--accent) 12%, transparent)", color: "var(--accent)",
  border: "1px solid color-mix(in srgb, var(--accent) 35%, var(--border))",
};
const detailBlock: React.CSSProperties = { padding: "12px 14px", background: "var(--surface-2)", borderRadius: "var(--r-sm)", border: "1px solid var(--border)" };
const detailLabel: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--body-faint)", marginBottom: 6 };
const passBadge: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: "var(--good)", textTransform: "none", letterSpacing: 0 };
const failBadge: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: "var(--bad)", textTransform: "none", letterSpacing: 0 };
const primaryBtn: React.CSSProperties = {
  background: "var(--accent)", color: "var(--accent-contrast)", border: "none", borderRadius: "var(--r-sm)",
  padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
};
const ghostBtn: React.CSSProperties = {
  background: "transparent", border: "1px solid var(--border)", color: "var(--accent)", borderRadius: "var(--r-sm)",
  padding: "8px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
};
